import { STANDBY_BOLT_ICON_SVG_SOURCE } from "@/app/game/standbyBoltIcon";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import { createEnabledHoverToggle } from "@/features/hoverIdle/mpm/enabledHoverToggle";
import type { Disposable } from "@/shared/contracts/lifecycle";

export interface StandbyNavigationToggle extends Disposable {
  readonly element: HTMLButtonElement;
  readonly standbyRoot: HTMLElement;
  prepareForStandby(): void;
  resetAfterStandby(): void;
}

interface CreateStandbyNavigationToggleOptions {
  readonly label: string;
  readonly pointerModel: HoverPointerModel;
  readonly getChargeMs: () => number;
  readonly onEnterStandby: () => void;
  readonly onResume: () => void;
}

interface CreateStandbyToggleOptions {
  readonly action: string;
  readonly additionalClassName: string;
  readonly label: string;
  readonly pointerModel: HoverPointerModel;
  readonly getChargeMs: () => number;
  readonly onEnabledChange: (enabled: boolean) => void;
}

function createStandbyToggle(options: CreateStandbyToggleOptions) {
  const toggle = createEnabledHoverToggle({
    pointerModel: options.pointerModel,
    action: options.action,
    ariaLabel: options.label,
    additionalClassName: options.additionalClassName,
    iconSource: STANDBY_BOLT_ICON_SVG_SOURCE,
    getChargeMs: options.getChargeMs,
    onEnabledChange: options.onEnabledChange,
  });
  toggle.element.title = options.label;
  return toggle;
}

export function createStandbyNavigationToggle(
  options: CreateStandbyNavigationToggleOptions,
): StandbyNavigationToggle {
  const standbyRoot = document.createElement("nav");
  standbyRoot.className =
    "hover-idle-nav-position hover-idle-standby-return-dock";
  standbyRoot.setAttribute("aria-label", options.label);
  standbyRoot.hidden = true;

  let navigationParent: HTMLElement | null = null;
  const toggle = createStandbyToggle({
    action: "standby-mode",
    additionalClassName: "hover-idle-nav-item hover-idle-nav-standby-toggle",
    label: options.label,
    pointerModel: options.pointerModel,
    getChargeMs: options.getChargeMs,
    onEnabledChange: (enabled) => {
      if (enabled) {
        options.onEnterStandby();
      } else {
        options.onResume();
      }
    },
  });
  toggle.sync({ enabled: false });

  return {
    element: toggle.element,
    standbyRoot,
    prepareForStandby(): void {
      const currentParent = toggle.element.parentElement;
      if (currentParent !== null && currentParent !== standbyRoot) {
        navigationParent = currentParent;
      }
      toggle.element.dataset.action = "resume-standby-mode";
      toggle.sync({ enabled: true });
      standbyRoot.append(toggle.element);
      standbyRoot.hidden = false;
    },
    resetAfterStandby(): void {
      standbyRoot.hidden = true;
      navigationParent?.append(toggle.element);
      toggle.element.dataset.action = "standby-mode";
      toggle.element.blur();
      toggle.sync({ enabled: false });
    },
    dispose(): void {
      toggle.dispose();
      standbyRoot.remove();
    },
  };
}
